/**
 * Parcours actif de la thématique consultée.
 *
 * Une thématique peut abriter plusieurs concours. Filtrer l'arbre affiché ne
 * suffit pas : le mélange, le quiz intelligent, les révisions, les erreurs et
 * les QRC tirent leurs questions côté serveur. Il faut donc que le même
 * périmètre voyage jusqu'aux requêtes.
 *
 * Plutôt que de faire descendre le choix par accessoires à travers six
 * composants, où un oubli est silencieux, on le range ici et l'intercepteur
 * d'`api.js` l'ajoute aux requêtes des points d'entrée listés ci-dessous. Cela
 * donne UN endroit à auditer.
 *
 * L'injection ne se fait que si la requête vise la thématique effectivement
 * consultée, comparée par identifiant ET par slug : sans cette vérification, un
 * appel concernant une autre thématique hériterait d'un parcours qui ne la
 * concerne pas.
 */

let actif = { scopeId: null, scopeSlug: null, slug: '' };

/** Appelé par la page de thématique à chaque rendu. */
export function setParcoursActif(scopeId, scopeSlug, slug) {
    actif = {
        scopeId: scopeId ? Number(scopeId) : null,
        scopeSlug: scopeSlug || null,
        slug: slug || '',
    };
}

export function getParcoursActif() {
    return actif;
}

/**
 * Points d'entrée dont la sélection dépend du parcours. Tout nouvel endpoint qui
 * tire des questions à l'échelle d'une thématique doit être ajouté ici.
 */
export const ENDPOINTS_PARCOURS = [
    '/qcm/examen-melange.php',
    '/user/learning-plan.php',
    '/qcm/session.php',
    '/qcm/questions.php',
    '/qcm/history-batch.php',
    '/qcm/smart-quiz.php',
    '/qcm/errors.php',
    '/qcm/sm2-due.php',
    '/qcm/qrc-list.php',
    '/qcm/qrc-audit.php',
    '/qrc/showcase.php',
];

/**
 * Parcours à joindre à cette requête, ou null.
 *
 * `cible` porte ce que la requête dit de la thématique : `scope_id`, `scope_slug`
 * ou `slug`. Sans correspondance, on n'ajoute rien.
 */
export function parcoursPourRequete(url, cible) {
    if (!actif.slug || !url) return null;
    if (cible && Object.prototype.hasOwnProperty.call(cible, 'parcours')) return null;
    if (url.includes('?') && new URLSearchParams(url.slice(url.indexOf('?') + 1)).has('parcours')) return null;
    if (!ENDPOINTS_PARCOURS.some(e => url.includes(e))) return null;
    // Plusieurs appels construisent leur chaîne de requête à la main plutôt que
    // de passer par `params` : il faut donc la lire dans l'URL aussi.
    let depuisUrl = {};
    const q = url.indexOf('?');
    if (q !== -1) {
        const p = new URLSearchParams(url.slice(q + 1));
        depuisUrl = { scope_id: p.get('scope_id'), scope_slug: p.get('scope_slug'), slug: p.get('slug') || p.get('scope') };
    }
    const id = cible?.scope_id ?? cible?.scopeId ?? depuisUrl.scope_id;
    const slug = cible?.scope_slug ?? cible?.scope ?? cible?.slug ?? depuisUrl.scope_slug ?? depuisUrl.slug;
    const memeScope = (id != null && Number(id) === actif.scopeId)
        || (slug != null && slug === actif.scopeSlug);
    return memeScope ? actif.slug : null;
}
